import * as React from 'react';
import { canMoveColumnBy, isDefaultLayout, mergeColumnsState, moveBounds, moveColumn, parseStoredColumns, resolveColumns, serializeColumns, setColumnPin, setColumnVisible, } from './columns-state.js';
/**
 * Ключ хранения настроек таблицы: `ui.table.<module>.<tableId>.v1`.
 * `module` — короткое имя модуля (`ads-payback`), `tableId` — таблица в нём.
 */
export function tableColumnsStorageKey(module, tableId) {
    return `ui.table.${module}.${tableId}.v1`;
}
// ── Внешнее хранилище на ключ ─────────────────────────────────────────────
// Источник правды — localStorage; useSyncExternalStore читает строку (строки
// сравниваются по значению — снимок стабилен без кэша). Если localStorage
// недоступен или запись упала, значение живёт в памяти до перезагрузки.
const memory = new Map();
const listeners = new Map();
let storageEventsBound = false;
function storage() {
    try {
        return globalThis.localStorage ?? null;
    }
    catch {
        return null;
    }
}
function readRaw(key) {
    if (memory.has(key))
        return memory.get(key) ?? null;
    try {
        return storage()?.getItem(key) ?? null;
    }
    catch {
        return null;
    }
}
function notify(key) {
    if (key === null) {
        for (const set of listeners.values())
            for (const cb of set)
                cb();
        return;
    }
    for (const cb of listeners.get(key) ?? [])
        cb();
}
function writeRaw(key, raw) {
    try {
        const ls = storage();
        if (!ls)
            throw new Error('localStorage недоступен');
        if (raw === null)
            ls.removeItem(key);
        else
            ls.setItem(key, raw);
        memory.delete(key);
    }
    catch {
        memory.set(key, raw);
    }
    notify(key);
}
function removeRaw(key) {
    try {
        storage()?.removeItem(key);
    }
    catch {
        // старый ключ остаётся — безвредно
    }
}
function onStorageEvent(e) {
    // Другая вкладка поменяла настройки — её значение главнее памяти этой.
    if (e.key === null)
        memory.clear();
    else
        memory.delete(e.key);
    notify(e.key);
}
function subscribeKey(key, cb) {
    let set = listeners.get(key);
    if (!set) {
        set = new Set();
        listeners.set(key, set);
    }
    set.add(cb);
    if (!storageEventsBound && typeof window !== 'undefined') {
        window.addEventListener('storage', onStorageEvent);
        storageEventsBound = true;
    }
    return () => {
        set.delete(cb);
        if (set.size === 0)
            listeners.delete(key);
    };
}
const noopSubscribe = () => () => { };
const nullSnapshot = () => null;
function readLegacy(legacy) {
    if (!legacy)
        return null;
    let raw;
    try {
        raw = storage()?.getItem(legacy.key) ?? null;
    }
    catch {
        return null;
    }
    if (raw === null)
        return null;
    try {
        const state = legacy.migrate(raw);
        return state ? serializeColumns(state) : null;
    }
    catch {
        return null;
    }
}
/**
 * Настройки колонок таблицы: видимость, порядок, закрепление — с сохранением
 * в localStorage под `storageKey` (см. `tableColumnsStorageKey`).
 *
 * - `storageKey: null` — без хранения (Storybook, тесты, временные таблицы).
 * - Два экземпляра с одним ключом и другие вкладки остаются согласованными.
 * - Битое или устаревшее значение — молча умолчание; неизвестные колонки не
 *   рисуются (но сохраняются), новые встают на своё место по умолчанию.
 * - `defs` должен быть стабильным (константа или `useMemo`).
 */
export function useTableColumns(storageKey, defs, options) {
    const [localRaw, setLocalRaw] = React.useState(null);
    const subscribe = React.useMemo(() => (storageKey === null ? noopSubscribe : (cb) => subscribeKey(storageKey, cb)), [storageKey]);
    const getSnapshot = React.useMemo(() => (storageKey === null ? nullSnapshot : () => readRaw(storageKey)), [storageKey]);
    const storedRaw = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const raw = storageKey === null ? localRaw : storedRaw;
    // Перенос из старого ключа: читаем один раз на ключ, пока новый пуст.
    const legacyRef = React.useRef(options?.legacy);
    legacyRef.current = options?.legacy;
    const needsLegacy = storageKey !== null && storedRaw === null && options?.legacy !== undefined;
    const legacyRaw = React.useMemo(() => (needsLegacy ? readLegacy(legacyRef.current) : null), [needsLegacy, storageKey]);
    React.useEffect(() => {
        if (storageKey === null || legacyRaw === null)
            return;
        writeRaw(storageKey, legacyRaw);
        const legacy = legacyRef.current;
        if (legacy)
            removeRaw(legacy.key);
    }, [storageKey, legacyRaw]);
    const effectiveRaw = raw ?? legacyRaw;
    const layout = React.useMemo(() => mergeColumnsState(defs, parseStoredColumns(effectiveRaw)), [defs, effectiveRaw]);
    const defsRef = React.useRef(defs);
    defsRef.current = defs;
    const legacyRawRef = React.useRef(legacyRaw);
    legacyRawRef.current = legacyRaw;
    /** Применить изменение к СВЕЖЕМУ состоянию (два вызова подряд не теряют первый). */
    const commit = React.useCallback((change) => {
        const next = (current) => {
            const d = defsRef.current;
            return serializeColumns(change(mergeColumnsState(d, parseStoredColumns(current)), d));
        };
        if (storageKey === null)
            setLocalRaw((prev) => next(prev));
        else
            writeRaw(storageKey, next(readRaw(storageKey) ?? legacyRawRef.current));
    }, [storageKey]);
    return React.useMemo(() => {
        const all = resolveColumns(layout, defs);
        const visible = all.filter((c) => c.visible);
        return {
            all,
            visible,
            visibleIds: visible.map((c) => c.id),
            isDefault: isDefaultLayout(layout, defs),
            setVisible: (id, v) => commit((l, d) => setColumnVisible(l, d, id, v)),
            setPin: (id, pin) => commit((l, d) => setColumnPin(l, d, id, pin)),
            move: (id, toIndex) => commit((l, d) => moveColumn(l, d, id, toIndex)),
            moveBy: (id, delta) => commit((l, d) => {
                const b = moveBounds(l, d, id);
                return b ? moveColumn(l, d, id, b.index + delta) : l;
            }),
            canMoveBy: (id, delta) => canMoveColumnBy(layout, defs, id, delta),
            reset: () => {
                if (storageKey === null)
                    setLocalRaw(null);
                else
                    writeRaw(storageKey, null);
            },
        };
    }, [layout, defs, commit, storageKey]);
}
/** Для тестов: забыть значения в памяти (когда localStorage был недоступен). */
export function resetTableColumnsMemory() {
    memory.clear();
}
//# sourceMappingURL=use-table-columns.js.map